import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
/** Jobs older than this are garbage-collected on rehydrate. */ const MAX_JOB_AGE_MS = 24 * 60 * 60 * 1000;
// In-memory set of batchIds explicitly dismissed this session. Not persisted —
// resets on full page load. Guards registerJob from resurrecting a job removed
// during the current React session (e.g. ProjectFilesSection re-mounting after
// the user clicks View in the tray). The reload path is covered separately by
// ProjectFilesSection gating trackContractBatch on an active batch status.
const dismissedBatchIds = new Set();
/** Clear the dismissed-ids set. Call in test beforeEach alongside setState. */ export function resetDismissedBatchIds() {
    dismissedBatchIds.clear();
}
// batchIds present in the store as of the last sessionStorage rehydrate — i.e.
// jobs that already existed before this page load, as opposed to ones
// registerJob adds fresh during the current session. A rehydrated job that
// immediately 404s (its batch row is gone server-side) is a stale leftover
// from a prior session, not a fresh processing failure — ProcessingJobsHost
// uses this to tell the two cases apart. Not persisted; rebuilt on each load.
const rehydratedBatchIds = new Set();
export function isRehydratedJob(batchId) {
    return rehydratedBatchIds.has(batchId);
}
/** Clear the rehydrated-ids set. Call in test beforeEach alongside setState. */ export function resetRehydratedBatchIds() {
    rehydratedBatchIds.clear();
}
export const useProcessingJobsStore = create()(persist((set)=>({
        jobs: {},
        registerJob: (job)=>{
            if (dismissedBatchIds.has(job.batchId)) return;
            set((state)=>({
                    jobs: {
                        ...state.jobs,
                        [job.batchId]: {
                            ...job,
                            status: 'processing',
                            percent: job.percent ?? 0,
                            startedAt: Date.now()
                        }
                    }
                }));
        },
        patchJob: (batchId, patch)=>set((state)=>{
                const existing = state.jobs[batchId];
                if (!existing) return state;
                return {
                    jobs: {
                        ...state.jobs,
                        [batchId]: {
                            ...existing,
                            ...patch
                        }
                    }
                };
            }),
        removeJob: (batchId)=>{
            dismissedBatchIds.add(batchId);
            set((state)=>{
                if (!state.jobs[batchId]) return state;
                const next = {
                    ...state.jobs
                };
                delete next[batchId];
                return {
                    jobs: next
                };
            });
        }
    }), {
    name: 'processing-jobs',
    storage: createJSONStorage(()=>sessionStorage),
    onRehydrateStorage: ()=>(state)=>{
            if (!state) return;
            const cutoff = Date.now() - MAX_JOB_AGE_MS;
            const fresh = Object.fromEntries(Object.entries(state.jobs).filter(([, job])=>job.startedAt > cutoff));
            if (Object.keys(fresh).length !== Object.keys(state.jobs).length) {
                state.jobs = fresh;
            }
            Object.keys(fresh).forEach((batchId)=>rehydratedBatchIds.add(batchId));
        }
}));
export function selectProcessingJobs(state) {
    return Object.values(state.jobs).filter((job)=>job.status === 'processing');
}
/** Find the tracked job for a pre-award workspace, regardless of status. */ export function selectJobForPreAward(state, preAwardProjectId) {
    return Object.values(state.jobs).find((job)=>job.meta?.preAwardProjectId === preAwardProjectId);
}
